import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, MutationCache, QueryCache } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import * as Sentry from "@sentry/react";
import { captureError } from "@/lib/logger";

import Auth from "./pages/Auth";
import PartnerLayout from "./components/layout/PartnerLayout";
import RequireAuth from "./components/auth/RequireAuth";
import Dashboard from "./pages/Dashboard";
import VehicleProcurement from "./pages/VehicleProcurement";
import VehicleContracts from "./pages/VehicleContracts";
import VehicleInspections from "./pages/VehicleInspections";

import EarningsRecords from "./pages/EarningsRecords";
import EarningsWeekDetail from "./pages/EarningsWeekDetail";
import EarningsVehicles from "./pages/EarningsVehicles";
import Payouts from "./pages/Payouts";
import PayoutWeekDetail from "./pages/PayoutWeekDetail";
import Agreements from "./pages/Agreements";
import NotFound from "./pages/NotFound";

/**
 * THE TWO LAZY ROUTES IN THIS APP, AND THEY ARE NOT AN OPTIMISATION.
 *
 * Both reach `pdfjs-dist`, which is several hundred kilobytes of parser plus a worker of its
 * own. Every other page here is imported eagerly, so a static import would put the whole PDF
 * reader in the entry chunk — paid for on first paint by every partner, on every visit, to
 * read a payout. `React.lazy` is what keeps it in a chunk fetched the moment somebody opens
 * an agreement and never before.
 *
 * `SignDocument` is lazy for the same reason and not quite the same one: the ceremony itself
 * does not paint a PDF, but it shares the reader, the markdown renderer and DOMPurify with
 * the viewer, and those are the heavy half. The two share the chunk.
 */
const SignDocument = lazy(() => import("./pages/SignDocument"));
const ViewAgreement = lazy(() => import("./pages/ViewAgreement"));

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
        },
    },
    queryCache: new QueryCache({
        onError: (error) => captureError(error),
    }),
    mutationCache: new MutationCache({
        onError: (error) => captureError(error),
    }),
});

const isDev = import.meta.env.DEV;

const ErrorFallback = ({ error }: { error: Error }) => (
    <div style={{ padding: '2rem', fontFamily: 'monospace' }}>
        <strong style={{ color: 'red' }}>Something went wrong.</strong>
        {isDev && (
            <pre style={{ marginTop: '1rem', whiteSpace: 'pre-wrap', fontSize: '0.8rem', color: '#666' }}>
                {error?.message}
                {'\n\n'}
                {error?.stack}
            </pre>
        )}
        {!isDev && <p>Our team has been notified.</p>}
    </div>
);

/** The chunk boundary made visible: the same skeleton the pages themselves open on. */
const AgreementChunkFallback = () => (
    <div className="mx-auto w-full max-w-2xl space-y-4 p-4">
        <div className="h-6 w-48 animate-pulse rounded-md bg-muted" />
        <div className="h-96 w-full animate-pulse rounded-md bg-muted" />
        <div className="h-10 w-full animate-pulse rounded-md bg-muted" />
    </div>
);

const App = () => (
    <Sentry.ErrorBoundary fallback={(props) => <ErrorFallback error={props.error as Error} />} onError={(error) => captureError(error)}>
        <QueryClientProvider client={queryClient}>
        <AuthProvider>
            <TooltipProvider>
                <Toaster />
                <Sonner />
                <BrowserRouter>
                    <Routes>
                        <Route path="/" element={<Navigate to="/overview/summary" replace />} />
                        <Route path="/auth" element={<Auth />} />

                        {/* Authenticated, but OUTSIDE the layout chrome — a ceremony is a
                            full-screen flow with nothing to wander off into mid-signature, and
                            the at-rest reader sits beside it for the same reason: reading an
                            executed instrument is a single-purpose thing.

                            THERE IS NO PUBLIC SIGNING ROUTE HERE [R2]. fleet-driver carries
                            `/sign/link` and a `/sign/witness` redirect because a witness holds
                            no account and signs from a token in the URL fragment. Every partner
                            who signs a Vehicle Schedule has a login, `agreement_sign`'s PARTNER
                            arm binds `my_partner_id()` and admits no token at all, and so no
                            credential ever travels in a URL in this app. See `src/sentry.ts`. */}
                        <Route element={<RequireAuth />}>
                            <Route
                                path="/sign/:documentId"
                                element={
                                    <Suspense fallback={<AgreementChunkFallback />}>
                                        <SignDocument />
                                    </Suspense>
                                }
                            />

                            {/* A bare `/agreement` is somebody who edited the address bar or
                                followed a truncated link; it goes to the agreements list rather
                                than to the 404, which would tell them their agreement does not
                                exist. */}
                            <Route path="/agreement" element={<Navigate to="/agreements" replace />} />
                            <Route
                                path="/agreement/:documentId"
                                element={
                                    <Suspense fallback={<AgreementChunkFallback />}>
                                        <ViewAgreement />
                                    </Suspense>
                                }
                            />
                        </Route>

                        <Route element={<PartnerLayout />}>
                            <Route path="/overview/summary" element={<Dashboard />} />
                            <Route path="/fleet/summary" element={<Navigate to="/fleet/procurement" replace />} />
                            <Route path="/fleet/procurement" element={<VehicleProcurement />} />
                            <Route path="/fleet/contracts" element={<VehicleContracts />} />
                            <Route path="/fleet/contracts/:contractId/inspections" element={<VehicleInspections />} />

                            <Route path="/finance/summary" element={<Navigate to="/finance/earnings" replace />} />
                            <Route path="/finance/earnings" element={<EarningsRecords />} />
                            <Route path="/finance/earnings/week" element={<EarningsWeekDetail />} />
                            <Route path="/finance/vehicles" element={<EarningsVehicles />} />
                            <Route path="/finance/payouts" element={<Payouts />} />
                            <Route path="/finance/payouts/week" element={<PayoutWeekDetail />} />

                            {/* The agreements list, INSIDE the chrome: it is a page a partner
                                browses, not a ceremony they are in the middle of. */}
                            <Route path="/agreements" element={<Agreements />} />
                        </Route>
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </BrowserRouter>
            </TooltipProvider>
        </AuthProvider>
    </QueryClientProvider>
    </Sentry.ErrorBoundary>
);

export default App;
